import {Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {EntityDatasource} from "../../api/entity.datasource";
import {Subscription} from "rxjs";
import {SearchService} from "../../services/search.service";
import {InternalPropertyMap} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {GeneralSearchFilter} from "../../basic-entity-back/filters/general-search-filter";
import {FilterAndData} from "../../api/filter-list";
import {RouteFilterTranslator} from "./route-filter-translator";
import {FilterDefinition, FilterGroup} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {Filter} from "../../api/filter-interface";
import {
    ExactSearchFilter, MultipleSearchFilter,
} from "../../basic-entity-back/filters/search-filter";
import {FilterListComponent} from "./filter-list/filter-list.component";
import {BasicEntityTableConstants} from "../basic-entity-table/basic-entity-table-constants";
import {clone} from "three/examples/jsm/utils/SkeletonUtils";
import {botonAplicado, filtrosAlPulsarBoton, filtrosConBusqueda, paramsDeFiltros, sinFiltrosDeBoton} from "./filtros-botones";

interface FilterRepresentation {
    name: string;
    icon?: string;
    description: string;
}

/**
 * This component filters the items in the indicated dataSource by all the possible
 * filters given the entities interface.
 * This component manages the query parameters and the general search in an stateless
 * way, using always the router to perform the changes, and listening to route changes
 * to reload the filtering in the DataSource.
 *
 * The params to filter in the route are preceded by "$f-" so they dont collide with
 * other params of the system.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
@Component({
    selector: "be-filtering-buttons",
    templateUrl: "./basic-entity-filtering-buttons.component.html",
    styleUrls: ["./basic-entity-filtering-buttons.component.scss"]
})
export class BasicEntityFilteringButtonsComponent<T> implements OnInit, OnDestroy {
    /** Time that the user has to stay without typing before the search is performed */
    private static readonly TIMEOUT_BEFORE_SEARCHING = 500;
    private static readonly FILTER_REPRESENTATION = new Map<Filter | FilterGroup, FilterRepresentation>([
        [ExactSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad es exactamente alguna de las palabras dadas (separando por espacios, si cabe).'
        }]
    ]);

    /** Properties to offer filters for */
    @Input() public properties: InternalPropertyMap[];
    /** Properties which can be filtered (obtained from the input properties) */
    public filtrableProperties: InternalPropertyMap[];
    /** The datasource this component should manage to make the filtering */
    @Input() protected dataSource: EntityDatasource<T>;
    @Input() protected customFilteringButtons: any[] = [];
    /**
     * Con panel de filtros en la tabla: pulsar un botón, TODOS o buscar conserva todo lo demás que haya en la
     * URL (el panel, la búsqueda y los fijos), en vez de volver a los filtros base de la primera carga. Lo pone
     * be-table cuando la tabla tiene panel (Piezas y, con el panel generado, Pedidos).
     */
    @Input() public conservarFiltros = false;
    /** Los filtros de la URL, leídos en cada cambio (solo se usan con conservarFiltros) */
    private _filtrosUrl: FilterAndData[] = [];

    @HostBinding('class.be-filtering') public classBiding = true;


    /** Timeout to reload the filters (while you type this timeout is renovated) */
    private _filtersReloadTimeout = null;
    /** Subscription to the general search in the search bar */
    private _searchSubscription: Subscription = null;
    private _queryParamsSubscription: Subscription = null;
    public selected: FilterAndData[];
    public defaultFilters: {};

    public static sFilterOrGroupRepr(filter: FilterDefinition): FilterRepresentation {
        const name = BasicEntityFilteringButtonsComponent.FILTER_REPRESENTATION.get(filter);
        return name || {
            icon: 'fa-exclamation',
            name: 'Filtro desconocido',
            description: 'Este filtro no se encuentra en la lista de filtros.'
        };
    }

    constructor(
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _searchService: SearchService,
        private _routeFilterTranslator: RouteFilterTranslator
    ) {
    }

    public filterOrGroupRepr(filter: Filter | FilterGroup): FilterRepresentation {
        return BasicEntityFilteringButtonsComponent.sFilterOrGroupRepr(filter);
    }

    ngOnInit() {
        this.filtrableProperties = this.properties.filter(prop => prop.filters.length > 0);
        this._searchService.hideSearch = false;
        this._searchSubscription = this._searchService.search.subscribe(
            newSearch => this._redirectGeneralSearch(newSearch)
        );
        this._queryParamsSubscription = this._activatedRoute
            .queryParams.subscribe(this._manageParamsChange.bind(this));
    }

    public filterChange(filter: FilterAndData[]) {
        this.selected = filter;
        if (this.conservarFiltros) {
            this._navegar(filtrosAlPulsarBoton(this._filtrosUrl, this.customFilteringButtons, filter));
            return;
        }
        this._filterNavigation(filter);
    }

    public filterCompare(f1: FilterAndData[], f2: FilterAndData[]) {
        if (!f1 || !f2 || f1.length !== f2.length) {
            return false;
        }

        return f1.every((item, index) => {
                return f1.length <= f2.length && item.filter.name === f2[index].filter.name && item.data.toString() === f2[index].data.toString()
            }
        );
    }

    private _manageParamsChange(params: Params, init: false) {
        const filters = {};
        Object.entries(this._routeFilterTranslator.paramsToFilters(params, this.properties))
            .filter(([k, f]) => f.data != null)
            .forEach(([k, f]) => {
                filters[k] = f
            });

        // Apply default filter if no filters are present in URL and it's not a clear action
        if (Object.keys(filters).length === 0 && this.customFilteringButtons.length > 0 && !params['cleared']) {
            const defaultButton = this.customFilteringButtons.find(b => b.default);
            if (defaultButton) {
                // Check if we are already showing this filter to avoid infinite loop
                if (!this.filterCompare(this.selected, defaultButton.filter)) {
                    this.selected = defaultButton.filter;
                    // We need to wait for the next tick to ensure query params are updated correctly if we are in ngOnInit cycle
                    // Con panel: como el resto de navegaciones de la tabla (con el tamaño de página) y sustituyendo la
                    // entrada del historial: si no, Atrás volvería a la URL vacía, que rebotaría aquí otra vez
                    setTimeout(() => this.conservarFiltros
                        ? this._navegar([...defaultButton.filter], false, true)
                        : this._filterNavigation(defaultButton.filter));
                    return;
                }
            }
        }

        // El botón marcado sale de la URL, así se ve también al volver de una edición o al recargar
        const boton = botonAplicado(this.customFilteringButtons, Object.values(filters));
        this.selected = boton ? boton.filter : null;
        if (!this.defaultFilters) {
            // Filtros base, los que se mantienen al pulsar un botón: lo que traía la URL menos el filtro
            // del propio botón (si no, el "Realizados hoy" por defecto se arrastraba a todos los demás)
            this.defaultFilters = {...sinFiltrosDeBoton(Object.values(filters), boton)};
        }
        this.dataSource.filtros = Object.values(filters);
        this._filtrosUrl = this.dataSource.filtros;
        const generalSearch = this.dataSource.filtros.find(f => f.filter === GeneralSearchFilter);
        if (generalSearch) {
            this._searchService.currentSearch = generalSearch.data;
        } else {
            this._searchService.currentSearch = null;
        }
        clearTimeout(this._filtersReloadTimeout);
        this._filtersReloadTimeout = setTimeout(
            () => this.dataSource.load(),
            BasicEntityFilteringButtonsComponent.TIMEOUT_BEFORE_SEARCHING);
    }

    /**
     * @inheritDoc
     */
    ngOnDestroy(): void {
        this._searchSubscription.unsubscribe();
        this._queryParamsSubscription.unsubscribe();
        // this._searchService.hideSearch = true;
    }

    /**
     * Performs the navigation to set the filter for the general search.
     * If str is null it does nothing.
     * @private
     */
    private _redirectGeneralSearch(str: string | null) {
        if (str == null) {
            return;
        }
        if (this.conservarFiltros) {
            this._navegar(filtrosConBusqueda(this._filtrosUrl, str));
            return;
        }

        if (str !== '') {
            this._filterNavigation([new FilterAndData(GeneralSearchFilter, str)]);
        } else {
            this._filterNavigation();
        }
    }

    private _filterNavigation(filterAndData: FilterAndData[] = null) {
        let filtered = {};
        let params: Params;
        let index = 0;
        if (this.defaultFilters) {
            Object.entries(this.defaultFilters)
                .filter(([k, f]) => f != null)
                .forEach(([k, f]) => {
                    filtered[k] = f;
                    index++;
                });
        }

        if (filterAndData) {
            filterAndData.forEach(f => {
                filtered[index] = f;
                index++;
            });
        }

        params = RouteFilterTranslator.sFiltersToParams(filtered);

        this._router.navigate([], {
            queryParams: params,
            queryParamsHandling: ""
        }).catch(reason =>
            console.error("Navigation rejected", reason)
        );
    }

    clearFilters() {
        if (this.conservarFiltros) {
            // TODOS quita solo el botón; `cleared` evita que vuelva el botón por defecto
            this.selected = null;
            this._navegar(filtrosAlPulsarBoton(this._filtrosUrl, this.customFilteringButtons, null), true);
            return;
        }
        this.selected = null;
        this.defaultFilters = null;
        this._router.navigate([], {
            queryParams: {cleared: true},
            queryParamsHandling: ""
        }).catch(reason =>
            console.error("Navigation rejected", reason)
        );
    }

    /** @param reemplazar sustituye la entrada del historial en vez de añadir otra (al aplicar el botón por defecto) */
    private _navegar(filtros: FilterAndData[], limpiado = false, reemplazar = false) {
        const params = paramsDeFiltros(filtros);
        if (this.dataSource.pagination.pageSize) {
            params[BasicEntityTableConstants.PAGINATION_SIZE] = this.dataSource.pagination.pageSize;
        }
        // Sin filtros también: si no, al quitar el último volverían los valores por defecto del panel
        if (limpiado || filtros.length === 0 || this._activatedRoute.snapshot.queryParams['cleared']) {
            params['cleared'] = true;
        }
        this._router.navigate([], {queryParams: params, queryParamsHandling: "", replaceUrl: reemplazar})
            .catch(reason => console.error("Navigation rejected", reason));
    }
}
